import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtDateTime } from "@/components/badges";
import { Copy, RotateCw, Trash2, UserPlus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useConfirm } from "@/components/ConfirmDialog";

export function EmployeesTab() {
  const { askConfirm, confirmDialog } = useConfirm();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const [empInviteForm, setEmpInviteForm] = useState({ email: "", name: "" });
  const [lastEmpLink, setLastEmpLink] = useState("");

  const { data: projects = [] } = useQuery({
    queryKey: ["admin-projects"],
    queryFn: async () => (await api.get("/admin/projects")).data,
  });
  const { data: empInvites = [] } = useQuery({
    queryKey: ["admin-emp-invites"],
    queryFn: async () => (await api.get("/admin/employee-invites")).data,
  });
  const { data: driveEmployees = [] } = useQuery({
    queryKey: ["admin-drive-employees"],
    queryFn: async () => (await api.get("/drive/employees")).data,
  });

  const empInviteMut = useMutation({
    mutationFn: async () => (await api.post("/admin/employee-invites", empInviteForm)).data,
    onSuccess: (d) => {
      toast.success(t("admin.toastEmpInviteSent", { email: d.email }));
      setLastEmpLink(d.link);
      setEmpInviteForm({ email: "", name: "" });
      qc.invalidateQueries({ queryKey: ["admin-emp-invites"] });
    },
    onError: (e) => toast.error(fmtErr(e)),
  });
  const delEmpInviteMut = useMutation({
    mutationFn: async (id) => api.delete(`/admin/employee-invites/${id}`),
    onSuccess: () => { toast.success(t("admin.toastInviteDeleted")); qc.invalidateQueries({ queryKey: ["admin-emp-invites"] }); },
    onError: (e) => toast.error(fmtErr(e)),
  });
  const resendInviteMut = useMutation({
    mutationFn: async (id) => api.post(`/admin/invites/${id}/resend`),
    onSuccess: () => {
      toast.success(t("admin.toastInviteResent"));
      qc.invalidateQueries({ queryKey: ["admin-invites"] });
      qc.invalidateQueries({ queryKey: ["admin-emp-invites"] });
    },
    onError: (e) => toast.error(fmtErr(e)),
  });
  const assignProjectsMut = useMutation({
    mutationFn: async ({ uid, project_ids }) => api.put(`/admin/users/${uid}/projects`, { project_ids }),
    onSuccess: () => {
      toast.success(t("admin.toastAssignmentsUpdated"));
      qc.invalidateQueries({ queryKey: ["admin-drive-employees"] });
    },
    onError: (e) => toast.error(fmtErr(e)),
  });
  const toggleEmployeeProject = (e, pid) => {
    const cur = new Set((e.projects || []).map((x) => x.project_id));
    if (cur.has(pid)) cur.delete(pid); else cur.add(pid);
    assignProjectsMut.mutate({ uid: e.id, project_ids: [...cur] });
  };
  const payAccessMut = useMutation({
    mutationFn: async ({ uid, can_view_payments }) => api.put(`/admin/users/${uid}/payments-access`, { can_view_payments }),
    onSuccess: () => {
      toast.success(t("admin.toastIpcAccess"));
      qc.invalidateQueries({ queryKey: ["admin-drive-employees"] });
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (e) => toast.error(fmtErr(e)),
  });
  const adminRoleMut = useMutation({
    mutationFn: async ({ uid, is_admin }) => api.put(`/admin/users/${uid}/admin-role`, { is_admin }),
    onSuccess: () => {
      toast.success(t("admin.toastAdminRole"));
      qc.invalidateQueries({ queryKey: ["admin-drive-employees"] });
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (e) => toast.error(fmtErr(e)),
  });
  const deleteUserMut = useMutation({
    mutationFn: async (id) => api.delete(`/admin/users/${id}`),
    onSuccess: () => {
      toast.success(t("admin.toastUserDeleted"));
      qc.invalidateQueries();
    },
    onError: (e) => toast.error(fmtErr(e)),
  });

  const copyLink = (link) => {
    navigator.clipboard.writeText(link).then(() => toast.success(t("admin.toastLinkCopied")));
  };

  return (
    <>
      <Card>
        <CardHeader><CardTitle className="text-base">{t("admin.empInviteCard")}</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-slate-500">{t("admin.empInviteNote")}</p>
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1.5">
              <Label>{t("common.email")}</Label>
              <Input className="w-72" placeholder="name@pruvapools.com" value={empInviteForm.email} onChange={(e) => setEmpInviteForm({ ...empInviteForm, email: e.target.value })} data-testid="employee-invite-email" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("admin.nameOpt")}</Label>
              <Input className="w-56" placeholder="Jane Doe" value={empInviteForm.name} onChange={(e) => setEmpInviteForm({ ...empInviteForm, name: e.target.value })} data-testid="employee-invite-name" />
            </div>
            <Button className="bg-slate-900 hover:bg-slate-800" onClick={() => empInviteMut.mutate()} disabled={!empInviteForm.email.trim() || empInviteMut.isPending} data-testid="employee-invite-submit">
              <UserPlus className="mr-2 h-4 w-4" /> {t("admin.sendEmpInvite")}
            </Button>
          </div>
          {lastEmpLink && (
            <div className="flex items-center gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs" data-testid="employee-invite-link-box">
              <span className="truncate font-mono text-sky-900">{lastEmpLink}</span>
              <Button size="sm" variant="outline" className="ml-auto h-7 shrink-0" onClick={() => copyLink(lastEmpLink)} data-testid="employee-invite-copy">
                <Copy className="mr-1 h-3.5 w-3.5" /> {t("shareDialog.copy")}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle className="text-base">{t("admin.employeesCard")}</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto p-0">
            <Table>
              <TableHeader><TableRow><TableHead>{t("common.name")}</TableHead><TableHead>{t("common.email")}</TableHead><TableHead>{t("admin.thProject")}s</TableHead><TableHead className="text-center">{t("admin.thIpcAccess")}</TableHead>{me?.is_super_admin && <TableHead className="text-center">{t("admin.thAdmin")}</TableHead>}<TableHead className="text-right">{t("admin.thDriveFiles")}</TableHead><TableHead className="w-14 text-right"></TableHead></TableRow></TableHeader>
              <TableBody>
                {driveEmployees.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-sm text-slate-500">{t("admin.noEmployees")}</TableCell></TableRow>}
                {driveEmployees.map((e) => (
                  <TableRow key={e.id} data-testid={`admin-employee-row-${e.id}`}>
                    <TableCell className="font-medium">{e.name || "—"}</TableCell>
                    <TableCell className="font-mono text-xs">{e.email}</TableCell>
                    <TableCell>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="sm" className="h-8 text-xs" data-testid={`employee-projects-${e.id}`}>
                            {(e.projects || []).length === 0 ? t("admin.assignProjects") : t("admin.projectsCount", { count: e.projects.length })}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-72 p-2" align="start">
                          <p className="px-2 pb-1.5 text-xs text-slate-500">{t("admin.toggleProjectsNote")}</p>
                          {(projects || []).map((p) => (
                            <label key={p.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-slate-50">
                              <Checkbox
                                checked={(e.projects || []).some((x) => x.project_id === p.id)}
                                onCheckedChange={() => toggleEmployeeProject(e, p.id)}
                                data-testid={`employee-project-toggle-${e.id}-${p.id}`}
                              />
                              <span className="min-w-0 flex-1 truncate">{p.name}</span>
                            </label>
                          ))}
                        </PopoverContent>
                      </Popover>
                      {(e.projects || []).length > 0 && (
                        <div className="mt-1 flex max-w-xs flex-wrap gap-1">
                          {e.projects.map((x) => (
                            <Badge key={x.project_id} variant="outline" className="font-mono text-[10px]">{x.project_name}</Badge>
                          ))}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {e.id !== me?.id && (
                        <Switch checked={!!e.can_view_payments} disabled={(e.projects || []).length === 0} onCheckedChange={(v) => payAccessMut.mutate({ uid: e.id, can_view_payments: v })} title={(e.projects || []).length === 0 ? t("admin.ipcAccessNeedProject") : t("admin.ipcAccessT")} data-testid={`employee-ipc-access-${e.id}`} />
                      )}
                    </TableCell>
                    {me?.is_super_admin && (
                    <TableCell className="text-center">
                      {e.id !== me?.id && (
                        <Switch checked={!!e.is_admin} onCheckedChange={(v) => adminRoleMut.mutate({ uid: e.id, is_admin: v })} title={t("admin.adminRoleT")} data-testid={`employee-admin-role-${e.id}`} />
                      )}
                    </TableCell>
                    )}
                    <TableCell className="text-right font-mono text-xs">{e.file_count}</TableCell>
                    <TableCell className="text-right">
                      {e.id !== me?.id && (
                        <Button variant="ghost" size="icon" title={t("admin.deleteEmpT")} className="text-rose-600" onClick={() => askConfirm(t("admin.deleteEmpConfirm", { email: e.email }), () => deleteUserMut.mutate(e.id))} data-testid={`delete-employee-${e.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">{t("admin.empInvitesCard")}</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto p-0">
            <Table>
              <TableHeader><TableRow><TableHead>{t("common.email")}</TableHead><TableHead>{t("admin.thSent")}</TableHead><TableHead>{t("common.status")}</TableHead><TableHead className="text-right">{t("admin.thLink")}</TableHead></TableRow></TableHeader>
              <TableBody>
                {empInvites.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-sm text-slate-500">{t("admin.empInvitesEmpty")}</TableCell></TableRow>}
                {empInvites.map((inv) => (
                  <TableRow key={inv.id} data-testid={`employee-invite-row-${inv.id}`}>
                    <TableCell className="font-mono text-xs">{inv.email}</TableCell>
                    <TableCell className="text-xs text-slate-500">{fmtDateTime(inv.created_at)}</TableCell>
                    <TableCell>{inv.used_at ? <Badge variant="outline" className="bg-emerald-50 text-emerald-700">{t("badges.accepted")}</Badge> : <Badge variant="outline" className="bg-amber-50 text-amber-700">{t("badges.pending")}</Badge>}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {inv.link && (
                          <Button size="sm" variant="ghost" onClick={() => copyLink(inv.link)} data-testid={`employee-invite-copy-${inv.id}`}>
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {!inv.accepted && (
                          <Button size="sm" variant="ghost" title={t("admin.resendInviteT")} onClick={() => resendInviteMut.mutate(inv.id)} disabled={resendInviteMut.isPending} data-testid={`employee-invite-resend-${inv.id}`}>
                            <RotateCw className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" title={t("admin.deleteEmpInviteT")} className="text-rose-600" onClick={() => delEmpInviteMut.mutate(inv.id)} data-testid={`employee-invite-delete-${inv.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
      {confirmDialog}
    </>
  );
}
